export const SPRITE_COUNT = 8;
export const SPRITE_WIDTH = 24;
export const SPRITE_HEIGHT = 21;
export const SPRITE_MULTICOLOR_WIDTH = 12;
export const SPRITE_ROW_BYTES = 3;
export const SPRITE_SLOT_BYTES = 64;

export type SpriteRenderSlot = {
  bytes: Uint8Array;
  color: number;
  multicolor: boolean;
};

export type SpriteCanvasMarker = {
  col: number;
  dashed?: boolean;
  row: number;
};

type Rgba = {
  a: number;
  b: number;
  g: number;
  r: number;
};

function rgb444ToRgba(value: number): Rgba {
  return {
    a: 255,
    b: (value & 0x0f) * 17,
    g: ((value >> 4) & 0x0f) * 17,
    r: ((value >> 8) & 0x0f) * 17
  };
}

function transparentCellRgba(row: number, col: number): Rgba {
  const light = (row + col) % 2 === 0 ? 24 : 36;
  return { r: light, g: light, b: light + 8, a: 255 };
}

function multicolorCellCode(slot: SpriteRenderSlot, row: number, col: number): number {
  const byteIndex = row * SPRITE_ROW_BYTES + (col >> 2);
  const shift = 6 - ((col & 0x03) * 2);
  return (slot.bytes[byteIndex] >> shift) & 0x03;
}

function monoCellCode(slot: SpriteRenderSlot, row: number, col: number): number {
  const byteIndex = row * SPRITE_ROW_BYTES + (col >> 3);
  const mask = 0x80 >> (col & 0x07);
  return (slot.bytes[byteIndex] & mask) !== 0 ? 2 : 0;
}

function spriteCellCode(slot: SpriteRenderSlot, row: number, col: number): number {
  return slot.multicolor ? multicolorCellCode(slot, row, col) : monoCellCode(slot, row, col);
}

function fillBlock(data: Uint8ClampedArray, canvasWidth: number, x: number, y: number, width: number, height: number, rgba: Rgba): void {
  for (let py = y; py < y + height; py += 1) {
    const rowBase = py * canvasWidth * 4;
    for (let px = x; px < x + width; px += 1) {
      const offset = rowBase + px * 4;
      data[offset] = rgba.r;
      data[offset + 1] = rgba.g;
      data[offset + 2] = rgba.b;
      data[offset + 3] = rgba.a;
    }
  }
}

export function logicalColumns(multicolor: boolean): number {
  return multicolor ? SPRITE_MULTICOLOR_WIDTH : SPRITE_WIDTH;
}

export function renderSpriteCanvas(
  canvas: HTMLCanvasElement,
  slot: SpriteRenderSlot,
  palette: Uint16Array,
  mc0: number,
  mc1: number,
  options?: {
    anchor?: SpriteCanvasMarker | null;
    cursor?: SpriteCanvasMarker | null;
    showGrid?: boolean;
  }
): void {
  const context = canvas.getContext("2d");
  if (context === null) {
    return;
  }

  const cols = logicalColumns(slot.multicolor);
  const cellWidth = Math.max(1, Math.floor(canvas.width / cols));
  const cellHeight = Math.max(1, Math.floor(canvas.height / SPRITE_HEIGHT));
  const width = cellWidth * cols;
  const height = cellHeight * SPRITE_HEIGHT;
  const image = context.createImageData(width, height);
  const spriteRgba = rgb444ToRgba(palette[slot.color] ?? 0);
  const mc0Rgba = rgb444ToRgba(palette[mc0] ?? 0);
  const mc1Rgba = rgb444ToRgba(palette[mc1] ?? 0);

  for (let row = 0; row < SPRITE_HEIGHT; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const code = spriteCellCode(slot, row, col);
      const rgba =
        code === 0
          ? transparentCellRgba(row, col)
          : code === 1
            ? mc0Rgba
            : code === 2
              ? spriteRgba
              : mc1Rgba;
      fillBlock(image.data, width, col * cellWidth, row * cellHeight, cellWidth, cellHeight, rgba);
    }
  }

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.imageSmoothingEnabled = false;
  context.putImageData(image, 0, 0);

  if (options?.showGrid ?? false) {
    context.save();
    context.strokeStyle = "rgba(8, 18, 25, 0.72)";
    context.lineWidth = 1;
    for (let col = 0; col <= cols; col += 1) {
      const x = col * cellWidth + 0.5;
      context.beginPath();
      context.moveTo(x, 0);
      context.lineTo(x, height);
      context.stroke();
    }
    for (let row = 0; row <= SPRITE_HEIGHT; row += 1) {
      const y = row * cellHeight + 0.5;
      context.beginPath();
      context.moveTo(0, y);
      context.lineTo(width, y);
      context.stroke();
    }
    context.restore();
  }

  const drawMarker = (marker: SpriteCanvasMarker | null | undefined, color: string): void => {
    if (marker === null || marker === undefined) {
      return;
    }
    const clampedCol = Math.max(0, Math.min(cols - 1, marker.col));
    const clampedRow = Math.max(0, Math.min(SPRITE_HEIGHT - 1, marker.row));
    context.save();
    context.strokeStyle = color;
    context.lineWidth = 2;
    if (marker.dashed) {
      context.setLineDash([5, 4]);
    }
    context.strokeRect(clampedCol * cellWidth + 1, clampedRow * cellHeight + 1, cellWidth - 2, cellHeight - 2);
    context.restore();
  };

  drawMarker(options?.anchor ?? null, "rgba(99, 212, 200, 0.95)");
  drawMarker(options?.cursor ?? null, "rgba(242, 184, 75, 0.98)");
}
